import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Swal2 from "sweetalert2";

// Lands here when a customer taps a WhatsApp magic-login link. Verifies the
// token and then reproduces the exact same localStorage/session state that a
// normal OTP login (SignInModal + ValidateModal) leaves behind, so Home,
// Checkout and OrderHistory all work immediately without a manual location pick.
export default function MagicLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const token = searchParams.get("token");
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    const showError = (message) => {
      Swal2.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title:
          message ||
          "This link is invalid or has expired. Please log in again.",
        showConfirmButton: false,
        timer: 3000,
        customClass: {
          popup: "me-small-toast",
          title: "me-small-toast-title",
        },
      });
      setTimeout(() => navigate("/", { replace: true }), 1500);
    };

    const verify = async () => {
      try {
        const res = await axios({
          url: "/User/magic-login",
          method: "get",
          baseURL: "https://dailydish.in/api",
          headers: { "content-type": "application/json" },
          params: { token },
        });

        if (res.status !== 200) {
          return showError();
        }

        const userData = res.data.details;
        const destinationRoutes = {
          home: "/home",
          myplan: "/my-plan",
          orders: "/orders",
        };
        const destinationPath =
          destinationRoutes[res.data.redirectTo] || "/home";
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("addresstype", "corporate");
        localStorage.removeItem("postLoginDestination");
        window.dispatchEvent(new Event("userUpdated"));

        // Resolve primaryAddress into a full address object, same as Validate.jsx does
        if (userData?.primaryAddress) {
          try {
            const response = await fetch(
              `https://dailydish.in/api/User/customers/${userData._id}/addresses/${userData.primaryAddress}/primary`,
              {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
              },
            );
            if (response.ok) {
              const addressData = await response.json();
              localStorage.setItem(
                "primaryAddress",
                JSON.stringify(addressData?.primaryAddress),
              );
            } else {
              localStorage.setItem(
                "primaryAddress",
                JSON.stringify(userData?.primaryAddress),
              );
            }
          } catch (fetchError) {
            localStorage.setItem(
              "primaryAddress",
              JSON.stringify(userData?.primaryAddress),
            );
          }
        } else {
          localStorage.setItem(
            "primaryAddress",
            JSON.stringify(userData?.primaryAddress),
          );
        }

        // Derive currentLocation from primaryAddress, same as SignInModal's
        // onVerificationSuccess does — Home's menu-loading fallback chain needs this.
        const primaryAddress = JSON.parse(
          localStorage.getItem("primaryAddress") || "null",
        );
        if (primaryAddress) {
          const locationData = {
            location: primaryAddress.location || {
              type: "Point",
              coordinates: [primaryAddress.lng || 0, primaryAddress.lat || 0],
            },
            fullAddress: primaryAddress.fullAddress || primaryAddress.address,
            hubName: primaryAddress.hubName || "",
            hubId: primaryAddress.hubId || "",
            isAutoDetected: false,
            timestamp: new Date().toISOString(),
          };
          localStorage.setItem("currentLocation", JSON.stringify(locationData));
          localStorage.setItem("locationManuallySelected", "true");
          window.dispatchEvent(new Event("locationUpdated"));
        }

        const currentLocation = JSON.parse(
          localStorage.getItem("currentLocation") || "null",
        );
        const hasAddresses =
          Array.isArray(userData.addresses) && userData.addresses.length > 0;

        setTimeout(() => {
          if (!hasAddresses || (!primaryAddress && !currentLocation)) {
            navigate("/location", { replace: true });
          } else {
            navigate(destinationPath, { replace: true });
          }
        }, 100);
      } catch (error) {
        showError(error.response?.data?.error);
      }
    };

    verify();
  }, [searchParams, navigate]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: "16px",
        background: "#F8F6F0",
      }}
    >
      <div
        className="spinner-border"
        style={{ color: "#6B8E23" }}
        role="status"
      />
      <p style={{ color: "#2C2C2C", fontFamily: "Inter, sans-serif" }}>
        Logging you in...
      </p>
    </div>
  );
}
